import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './sidebar.js';
import { Topbar } from './topbar.js';
import { RouteErrorBoundary } from './error-boundary.js';
import { useApiQuery, useVisiblePolling } from '../api/hooks.js';
import type { ApiClientOptions, ApiResult } from '../api/client.js';
import {
  fetchOverviewAlarms,
  type OverviewAlarm,
  type OverviewAlarmsResponse,
} from '../api/overview.js';
import { useCan } from '../session/can.js';
import { describeProblem } from '../i18n/overview-problems.js';
import { ago } from '../i18n/activity-kinds.js';
import { providerLabel, providerLabels } from '../providers/catalog.js';
import { Button, Icon } from '../ui/index.js';
import {
  IncidentActions,
  IncidentRow,
  applyIncidentPatches,
  isUnacknowledgedCritical,
  sortIncidents,
  type IncidentPatch,
  type TimedPatch,
} from './incidents.js';
import '../styles/incidents.css';

/** Global alarm banner poll cadence (reads the cached evaluator state only). */
export const ALARM_POLL_MS = 60_000;

/**
 * 2026-09-22 — the shell polls GET /admin/overview/alarms once for every page
 * and shares the result: the banner above <Outlet/>, the topbar bell and the
 * overview's "Açık olaylar" / "alarm servisi" read the same state.
 * 2026-09-28 (D-243) — every alarm is an incident; `applyMark` overlays a
 * mark the server just accepted (Okundu / İncelendi / Ertele) until the next
 * evaluator tick confirms it.
 */
export interface GlobalAlarmsState {
  /**
   * Last successful read (with fresh local marks overlaid). `null` = unknown
   * (no capability, endpoint missing/denied on the first read, or not loaded
   * yet).
   */
  readonly data: OverviewAlarmsResponse | null;
  /**
   * A later poll failed (network, timeout, 5xx, 403/404 after a rollback…):
   * `data` is the last successful read, NOT the current state. Never silently
   * stale — every surface marks it.
   */
  readonly stale: boolean;
  /** Client time (ISO) of the last successful read. */
  readonly lastOkAt: string | null;
  /** Overlay a server-accepted mark (absent outside the shell, e.g. in page tests). */
  readonly applyMark?: (incidentId: string, patch: IncidentPatch) => void;
}

export const UNKNOWN_ALARMS: GlobalAlarmsState = { data: null, stale: false, lastOkAt: null };

const OverviewAlarmsContext = createContext<GlobalAlarmsState>(UNKNOWN_ALARMS);
export const OverviewAlarmsProvider = OverviewAlarmsContext.Provider;

export function useOverviewAlarms(): GlobalAlarmsState {
  return useContext(OverviewAlarmsContext);
}

const ALARMS_DISABLED: ApiResult<OverviewAlarmsResponse> = {
  kind: 'error',
  status: 0,
  reason: 'alarms_disabled',
  body: null,
};

function useGlobalAlarms(enabled: boolean): GlobalAlarmsState {
  const [lastOkAt, setLastOkAt] = useState<string | null>(null);
  const [patches, setPatches] = useState<ReadonlyMap<string, TimedPatch>>(() => new Map());
  const runner = useCallback(
    async (o: ApiClientOptions): Promise<ApiResult<OverviewAlarmsResponse>> => {
      if (!enabled) return ALARMS_DISABLED;
      const res = await fetchOverviewAlarms(o);
      if (res.kind === 'ok' && !o.signal?.aborted) setLastOkAt(new Date().toISOString());
      return res;
    },
    [enabled],
  );
  const query = useApiQuery<OverviewAlarmsResponse>(runner, [enabled]);
  useVisiblePolling(query.reload, ALARM_POLL_MS, enabled);
  const applyMark = useCallback((incidentId: string, patch: IncidentPatch) => {
    setPatches((prev) => {
      const next = new Map(prev);
      next.set(incidentId, { patch, at: Date.now() });
      return next;
    });
  }, []);
  // A FIRST-read 401/403/404/network/malformed lands in `error` (or
  // auth_required): the banner simply stays hidden — an optional endpoint must
  // never break a page. After a success, useApiQuery keeps the old data and
  // only sets `refreshError`; that is surfaced as `stale`, never hidden.
  const raw = enabled && query.kind === 'ok' ? query.data : null;
  const data = useMemo(() => applyIncidentPatches(raw, patches, Date.now()), [raw, patches]);
  const stale = data !== null && query.refreshError !== null;
  const okAt = data !== null ? lastOkAt : null;
  return useMemo(() => ({ data, stale, lastOkAt: okAt, applyMark }), [data, stale, okAt, applyMark]);
}

/**
 * Bell count (red): unacknowledged, un-snoozed CRITICAL incidents, plus one
 * when the evaluator itself is silent. Acknowledged / snoozed / warn-level
 * incidents are counted separately (quietAlarmCount).
 */
export function activeAlarmCount(alarms: OverviewAlarmsResponse | null, nowMs = Date.now()): number | null {
  if (!alarms) return null;
  return alarms.alarms.filter((a) => isUnacknowledgedCritical(a, nowMs)).length + (alarms.evaluatorAlive ? 0 : 1);
}

/** Open incidents that are not "unread": acknowledged, snoozed or warn-level (grey bell badge). */
export function quietAlarmCount(alarms: OverviewAlarmsResponse | null, nowMs = Date.now()): number | null {
  if (!alarms) return null;
  return alarms.alarms.filter((a) => !isUnacknowledgedCritical(a, nowMs)).length;
}

/**
 * Red when an unacknowledged critical incident is open or the evaluator is
 * silent — the server emits `evaluator_silent` as critical, so banner, bell
 * and the overview agree.
 */
export function alarmsCritical(alarms: OverviewAlarmsResponse | null, nowMs = Date.now()): boolean {
  if (!alarms) return false;
  return !alarms.evaluatorAlive || alarms.alarms.some((a) => isUnacknowledgedCritical(a, nowMs));
}

/** One banner line: Turkish title, the providers it concerns, and its age. */
export function alarmLine(alarm: OverviewAlarm): string {
  const ids = alarm.providerIds;
  const single = ids.length === 1 ? ids[0] ?? null : null;
  const text = describeProblem(alarm.code, alarm.params, single);
  // Provider-scoped codes already prefix the title with the label.
  const titled = single !== null && text.title.includes(providerLabel(single));
  const scope = ids.length > 0 && !titled ? ` — ${providerLabels(ids)}` : '';
  const since = alarm.since ? ` · ${ago(alarm.since)} başladı` : '';
  return `${text.title}${scope}${since}`;
}

export interface AlarmBannerProps {
  readonly alarms: OverviewAlarmsResponse | null;
  /** The latest alarms poll failed: `alarms` is the last successful read. */
  readonly stale?: boolean;
  /** Client time (ISO) of the last successful read (for the stale line). */
  readonly lastOkAt?: string | null;
  /** Show the "Genel Bakış'ta gör" link (the banner is not rendered on the overview at all). */
  readonly showOverviewLink: boolean;
  /** Overlay a mark the server accepted (the shell's applyMark). */
  readonly onMarked?: (incidentId: string, patch: IncidentPatch) => void;
}

const MAX_BANNER_LINES = 3;

/**
 * D-243: the banner shows ONLY unacknowledged, un-snoozed CRITICAL incidents
 * (plus "evaluator silent" and a failed refresh). Warn-level and
 * informational signals never reach it; an admin with provider:inspect can
 * mark a line "Okundu" right here.
 */
export function AlarmBanner({
  alarms,
  stale = false,
  lastOkAt = null,
  showOverviewLink,
  onMarked,
}: AlarmBannerProps): JSX.Element | null {
  if (!alarms) return null;
  const nowMs = Date.now();
  const critical = alarms.alarms.filter((a) => isUnacknowledgedCritical(a, nowMs));
  const evaluatorDead = !alarms.evaluatorAlive;
  // A failed refresh after an all-clear must not read as "all clear".
  if (critical.length === 0 && !evaluatorDead && !stale) return null;

  // evaluator_silent is critical on the server too (overview + bell agree).
  const tone = critical.length > 0 || evaluatorDead ? 'critical' : 'warn';
  const shown = critical.slice(0, MAX_BANNER_LINES);
  const more = critical.length - shown.length;
  const heading =
    critical.length > 0
      ? `${critical.length} okunmamış kritik olay`
      : evaluatorDead
        ? 'Alarm servisi yanıt vermiyor'
        : 'Alarm durumu okunamadı';
  const lastEval = alarms.evaluatedAt ? ` (son değerlendirme ${ago(alarms.evaluatedAt)})` : '';

  return (
    <div
      className={`ov-alarm-banner ov-alarm-${tone}`}
      role="alert"
      data-testid="global-alarm-banner"
      data-tone={tone}
      data-stale={stale ? 'true' : undefined}
    >
      <span className="ov-alarm-icon" aria-hidden="true">
        <Icon.Warn size={16} />
      </span>
      <div className="ov-alarm-body">
        <div className="ov-alarm-heading">{heading}</div>
        {shown.length > 0 ? (
          <ul className="ov-alarm-list">
            {shown.map((a) => (
              <li key={a.id} className="inc-banner-line" data-testid={`global-alarm-line-${a.id}`}>
                <span className="inc-banner-text">{alarmLine(a)}</span>
                <IncidentActions alarm={a} acknowledgeOnly onMarked={onMarked} testIdPrefix="banner" />
              </li>
            ))}
            {more > 0 ? <li className="ov-alarm-more">+{more} olay daha</li> : null}
          </ul>
        ) : null}
        {evaluatorDead ? (
          <div className="ov-alarm-evaluator" data-testid="global-alarm-evaluator-dead">
            {shown.length > 0
              ? `Alarm servisi yanıt vermiyor — gösterilen olaylar güncel olmayabilir${lastEval}.`
              : `Yeni sorunlar fark edilmiyor ve alarm e-postası gitmiyor${lastEval}.`}
          </div>
        ) : null}
        {stale ? (
          <div className="ov-alarm-evaluator" data-testid="global-alarm-stale">
            Alarm durumu yenilenemiyor; gösterilen bilgi güncel olmayabilir
            {lastOkAt ? ` (son başarılı okuma ${ago(lastOkAt)})` : ''}.
          </div>
        ) : null}
      </div>
      {showOverviewLink ? (
        <Link to="/" className="ov-alarm-link">
          Genel Bakış'ta gör
          <Icon.ChevronRight size={12} />
        </Link>
      ) : null}
    </div>
  );
}

/**
 * 2026-09-28 (D-243) — the bell's dropdown: every open incident (unacknowledged
 * critical first) with Okundu / İncelendi / Ertele, and the evaluator /
 * store notes.
 */
export function IncidentBellPanel({
  state,
  onClose,
}: {
  readonly state: GlobalAlarmsState;
  readonly onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const data = state.data;
  const nowMs = Date.now();
  const list = data ? sortIncidents(data.alarms, nowMs) : [];
  return (
    <div className="inc-bell-panel" data-testid="topbar-incidents" role="dialog" aria-label="Açık olaylar">
      <div className="inc-bell-head">
        <strong>Açık olaylar</strong>
        {data ? <span className="ov-muted ov-xs">{list.length} olay</span> : null}
      </div>
      {data === null ? (
        <p className="inc-bell-note">Alarm durumu okunamadı.</p>
      ) : (
        <>
          {!data.evaluatorAlive ? (
            <p className="inc-bell-note inc-bell-note--bad">
              Alarm servisi yanıt vermiyor; yeni sorunlar fark edilmiyor ve e-posta gitmiyor.
            </p>
          ) : null}
          {state.stale ? (
            <p className="inc-bell-note">Alarm durumu yenilenemiyor; liste güncel olmayabilir.</p>
          ) : null}
          {data.incidentStore === 'unavailable' ? (
            <p className="inc-bell-note" data-testid="topbar-incidents-store">
              Olay kaydı şu an kullanılamıyor: okundu / ertele işaretlenemez.
            </p>
          ) : null}
          {list.length === 0 ? (
            <p className="inc-bell-note" data-testid="topbar-incidents-empty">
              Açık olay yok.
            </p>
          ) : (
            <ul className="inc-list inc-list--bell">
              {list.map((a) => (
                <IncidentRow key={a.id} alarm={a} nowMs={nowMs} onMarked={state.applyMark} compact testIdPrefix="bell" />
              ))}
            </ul>
          )}
        </>
      )}
      <div className="inc-bell-foot">
        <Button
          size="sm"
          variant="ghost"
          iconRight={<Icon.ChevronRight size={12} />}
          onClick={() => {
            onClose();
            navigate('/');
          }}
          data-testid="topbar-incidents-overview"
        >
          Genel Bakış'ta gör
        </Button>
      </div>
    </div>
  );
}

/**
 * V2 admin shell — header on top, sidebar+main flex below.
 * On <880px the sidebar collapses to a hamburger-triggered drawer.
 * Route changes auto-close the drawer so navigation doesn't leave it
 * hovering over the main content.
 */
export function AppShell(): JSX.Element {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const location = useLocation();
  const canViewHealth = useCan('provider:view_health');
  const alarms = useGlobalAlarms(canViewHealth);
  // D-243: the overview shows the incidents inline — no banner there.
  const onOverview = location.pathname === '/';

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const toggleSidebar = useCallback(() => setSidebarOpen((o) => !o), []);
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const bellPanel = useCallback(
    (close: () => void) => <IncidentBellPanel state={alarms} onClose={close} />,
    [alarms],
  );

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--bg-page)',
      }}
    >
      <Topbar
        onToggleSidebar={toggleSidebar}
        alarmCount={activeAlarmCount(alarms.data)}
        alarmQuietCount={quietAlarmCount(alarms.data)}
        alarmCritical={alarmsCritical(alarms.data)}
        alarmStale={alarms.stale}
        bellPanel={canViewHealth ? bellPanel : undefined}
      />
      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        <Sidebar open={sidebarOpen} onClose={closeSidebar} />
        <main
          className="main"
          style={{
            flex: 1,
            minWidth: 0,
            overflow: 'auto',
          }}
        >
          <OverviewAlarmsProvider value={alarms}>
            {!onOverview ? (
              <AlarmBanner
                alarms={alarms.data}
                stale={alarms.stale}
                lastOkAt={alarms.lastOkAt}
                showOverviewLink
                onMarked={alarms.applyMark}
              />
            ) : null}
            <RouteErrorBoundary>
              <Outlet />
            </RouteErrorBoundary>
          </OverviewAlarmsProvider>
        </main>
      </div>
    </div>
  );
}
